import { useState, useCallback, useEffect } from 'react';
import { useCustomToast } from '@/hooks/use-custom-toast';
import { useFileUpload } from '@/hooks/use-file-upload';
import { useUploadFileMutation } from '@/features/_shared/api/upload-file.api';
import { axiosExtractErrorMessage, AxiosErrorCustom } from '@/lib/axios';
import { FileWithPreview } from '@/features/_shared/hooks/use-file-upload';
import { TFileItem } from '@/features/_shared/types/upload-file.type';

/**
 * Type definitions for form attachments
 */
export type FormAttachment = {
  fileName: string;
  size: number;
  uri: string;
  contentType: string;
};

/**
 * Helper function to map form attachments to TFileItem format
 */
const mapFormAttachmentToFileItem = (attachment: FormAttachment): TFileItem => ({
  filename: attachment.fileName,
  key: attachment.uri,
  url: attachment.uri,
  filesize: attachment.size,
  mimetype: attachment.contentType,
});

/**
 * Helper function to map TFileItem to form attachment format
 */
const mapFileItemToFormAttachment = (file: TFileItem): FormAttachment => ({
  fileName: file.filename,
  size: file.filesize,
  uri: file.url,
  contentType: file.mimetype,
});

interface UseFileAttachmentProps {
  initialAttachments?: FormAttachment[];
  onAttachmentsChange?: (attachments: FormAttachment[]) => void;
  onError?: (error: string) => void;
}

export function useFileAttachment({
  initialAttachments = [],
  onAttachmentsChange,
  onError,
}: UseFileAttachmentProps = {}) {
  // ===== Hooks =====
  const showToast = useCustomToast();

  // ===== State =====
  const [attachments, setAttachments] = useState<FormAttachment[]>(initialAttachments);
  const [extractedAttachments, setExtractedAttachments] = useState<TFileItem[]>(
    initialAttachments.map(mapFormAttachmentToFileItem)
  );
  const [uploadingFileIds, setUploadingFileIds] = useState<string[]>([]);
  const [fileError, setFileError] = useState<string | null>(null);

  // ===== Effects =====
  // Sync initial attachments when they change externally
  useEffect(() => {
    setAttachments(initialAttachments);
    setExtractedAttachments(initialAttachments.map(mapFormAttachmentToFileItem));
  }, [initialAttachments]);

  // ===== File Upload Hook =====
  const { clearFile } = useFileUpload({
    multiple: true,
    acceptedFileTypes: ['.p8', '.pdf', '.doc', '.docx', '.txt', '.jpg', '.png', '.gif'],
    onFileSelect: () => {
      setFileError(null);
    },
    onError: (error) => {
      setFileError(error);
      onError?.(error);
    },
  });

  // ===== Mutations =====
  // Upload Files
  const mutationUploadFiles = useUploadFileMutation({
    mutationConfig: {
      onError: (error: AxiosErrorCustom) => {
        const errorMessage = axiosExtractErrorMessage(error) || 'Error upload file, please try again';
        showToast({ message: errorMessage, type: 'error' });
        setUploadingFileIds([]);
        onError?.(errorMessage);
      },
      onSuccess: (data) => {
        const files = data.payload.files as TFileItem[];

        // Map uploaded files to form attachment format
        const mappedFiles = files.map(mapFileItemToFormAttachment);

        // Filter out duplicates based on URI
        const existingUris = new Set(attachments.map((att) => att.uri));
        const newFiles = mappedFiles.filter((file) => !existingUris.has(file.uri));

        // Update state with new files
        if (newFiles.length > 0) {
          const updatedAttachments = [...attachments, ...newFiles];
          setAttachments(updatedAttachments);
          setExtractedAttachments(updatedAttachments.map(mapFormAttachmentToFileItem));
          onAttachmentsChange?.(updatedAttachments);
        }

        showToast({ message: 'Files uploaded successfully', type: 'success' });
        setUploadingFileIds([]);
      },
    },
  });

  // ===== Handlers =====
  // Add Files
  const handleAddFiles = useCallback(
    (files: FileWithPreview[]) => {
      const filesToUpload = files.map((file) => file.file);
      const fileIds = files.map((file) => file.id);
      setUploadingFileIds(fileIds);
      mutationUploadFiles.mutate({ files: filesToUpload as File[] });
    },
    [mutationUploadFiles]
  );

  // Remove File
  const handleRemoveFile = useCallback(
    (removedFile: FileWithPreview) => {
      // Remove from uploading files if present
      setUploadingFileIds((prev) => prev.filter((id) => id !== removedFile.id));

      // Filter out the removed file from attachments
      const filteredAttachments = attachments.filter((attachment) => attachment.uri !== removedFile.preview);

      // Update state
      setAttachments(filteredAttachments);
      setExtractedAttachments(filteredAttachments.map(mapFormAttachmentToFileItem));
      onAttachmentsChange?.(filteredAttachments);
    },
    [attachments, onAttachmentsChange]
  );

  // Set Attachments (useful for syncing with external form)
  const setAttachmentsFromExternal = useCallback((newAttachments: FormAttachment[]) => {
    setAttachments(newAttachments);
    setExtractedAttachments(newAttachments.map(mapFormAttachmentToFileItem));
  }, []);

  // Reset attachments
  const resetAttachments = useCallback(() => {
    setAttachments([]);
    setExtractedAttachments([]);
    setFileError(null);
    onAttachmentsChange?.([]);
  }, [onAttachmentsChange]);

  // ===== Return =====
  return {
    // Attachments in both formats
    attachments, // FormAttachment[] format
    extractedAttachments, // TFileItem[] format
    uploadingFileIds,
    isUploadingFiles: Boolean(mutationUploadFiles?.isPending),
    fileError,
    // Handlers
    handleAddFiles,
    handleRemoveFile,
    setAttachments: setAttachmentsFromExternal,
    resetAttachments,
    clearFile,
  };
}

export type UseFileAttachmentReturn = ReturnType<typeof useFileAttachment>;
